import { useState, useEffect, useRef } from 'react';

/**
 * Hook to detect if the user is idle
 * @param timeout Timeout in milliseconds (default: 30000ms / 30s)
 * @returns boolean indicating if the user is idle
 */
const useIdle = (timeout: number = 30000): boolean => {
  const [isIdle, setIsIdle] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleActivity = () => {
      setIsIdle(false);
      
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        setIsIdle(true);
      }, timeout);
    };

    // Initial timer start
    handleActivity();

    // Events to listen for
    const events = [
      'mousemove',
      'mousedown',
      'keypress',
      'DOMMouseScroll',
      'mousewheel',
      'touchmove',
      'MSPointerMove',
      'scroll' // Also listen for scroll
    ];

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      // Cleanup
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [timeout]);

  return isIdle;
};

export default useIdle;
